/**
 * REST client for the WhatsApp Inbox backend (app/api/v1/whatsapp_conversations.py).
 *
 * Follows the same pattern as whatsappCloudService.ts (raw axios + ApiEnvelope +
 * cookie-based bearer token) rather than the generic src/utils/api client, so
 * this stays consistent with the sibling WhatsApp service rather than mixing
 * two calling conventions in the same feature area.
 */
import axios from 'axios';
import Cookies from 'js-cookie';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';

// ---- Types ---------------------------------------------------------------

export type ConversationStatus = 'open' | 'resolved' | 'archived';

export interface WhatsAppConversation {
  id: number;
  uuid: string;
  lead_id_fk: number;
  lead_uuid: string | null;
  wa_phone: string | null;
  status: ConversationStatus;
  is_muted: boolean;
  opted_out: boolean;
  unread_count: number;
  /** The customer wrote last and is waiting for our reply (server-computed). */
  awaiting_reply?: boolean;
  /** List priority: 1 waiting + window open, 2 window open, 3 window closed. */
  priority_group?: number;
  last_inbound_at: string | null;
  last_outbound_at: string | null;
  last_message_at: string | null;
  last_message_preview: string | null;
  lead_name: string | null;
  owner_uuid: string | null;
  owner_name: string | null;
  lead_bucket: 'abandoned_cart' | 'reorder' | 'rto' | 'phone' | string | null;
}

export interface WhatsAppLeadSearchResult {
  lead_id: number;
  uuid: string;
  name: string | null;
  phone: string | null;
  /** Present when this lead already has a conversation — pick it and open
   * the existing thread instead of starting a duplicate. */
  conversation_uuid: string | null;
}

export interface StartConversationResult extends SendResult {
  conversation_uuid?: string;
}

export type MessageDirection = 'inbound' | 'outbound';

export interface WhatsAppMessage {
  id: number;
  uuid: string;
  direction: MessageDirection;
  wa_from: string | null;
  wa_to: string | null;
  msg_type: string | null;
  body: string | null;
  media_id: string | null;
  media_mime: string | null;
  media_storage_key: string | null;
  context_message_id: string | null;
  status: 'sending' | 'sent' | 'delivered' | 'read' | 'failed' | null;
  status_at: string | null;
  error_code: string | null;
  error_title: string | null;
  created_at: string;
  /** Reconstructed for msg_type 'template' only — the actual image/video/document
   * header that was sent, and the resolved (variables filled in) button list. */
  template_header_image_url: string | null;
  template_buttons: { type: string; text?: string; url?: string; phone_number?: string }[];
  /** Who sent it. Derived server-side from what was already recorded at send time,
   * so no column was added: the agent's name, the journey's campaign name, or the
   * customer's. Null only for pre-Inbox rows with no sender on record at all —
   * render nothing there rather than guessing. */
  sent_by_name: string | null;
  sent_by_kind: 'agent' | 'automation' | 'customer' | 'unknown';
}

export interface WhatsAppThread {
  conversation_id: number;
  messages: WhatsAppMessage[];
  has_more: boolean;
}

export interface WhatsAppInternalNote {
  note_id: number;
  uuid: string;
  note_text: string;
  user_id_fk: number;
  user_uuid: string;
  created_at: string;
}

export interface ListConversationsParams {
  status?: ConversationStatus;
  unread_only?: boolean;
  assigned_user_uuid?: string;
  search?: string;
  /** Look up the one conversation for this lead — Lead Detail deep link (FE-20). */
  lead_uuid?: string;
  page?: number;
  page_size?: number;
}

export interface SendResult {
  ok: boolean;
  message_id?: string;
  error?: string;
  /** True when the 24h session window is closed — composer should switch to
   * the template picker rather than just showing a generic error. */
  window_closed?: boolean;
}

export type MediaMessageType = 'image' | 'video' | 'audio' | 'document';

export interface QuickContextOrderStatus {
  order_uuid: string;
  order_name: string | null;
  total_price: number | null;
  order_date: string | null;
  latest_delivery_status: string | null;
  latest_delivery_status_desc: string | null;
  latest_delivery_location: string | null;
}

export interface QuickContextReorder {
  last_order_date: string | null;
  days_since_order: number;
  reorder_due_date: string | null;
  due: boolean;
}

export interface QuickContextAbandonedCart {
  item_count: number;
  total: number;
  items: { title: string | null; variant_title: string | null; quantity: number | null; line_item_total: number | null }[];
}

export interface QuickContext {
  order_status: QuickContextOrderStatus | null;
  reorder: QuickContextReorder | null;
  abandoned_cart: QuickContextAbandonedCart | null;
}

export interface CatalogueProductVariant {
  id: string;
  title: string;
  price: string | null;
}

export interface CatalogueProduct {
  product_id: number;
  uuid: string;
  title: string;
  handle: string | null;
  description: string;
  image_url: string | null;
  variants: CatalogueProductVariant[];
}

interface ApiEnvelope<T> {
  success: boolean;
  message?: string;
  data: T;
  total?: number;
  totalPages?: number;
}

// ---- Service -------------------------------------------------------------

class WhatsAppInboxService {
  private base = `${API_BASE_URL}/whatsapp-conversations`;

  private getAuthHeaders() {
    let token = Cookies.get('crm_auth_token');
    if (!token && import.meta.env.VITE_BEARER_TOKEN) {
      token = import.meta.env.VITE_BEARER_TOKEN;
    }
    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  async listConversations(
    params: ListConversationsParams = {},
  ): Promise<{ data: WhatsAppConversation[]; total: number; totalPages: number }> {
    const res = await axios.get<ApiEnvelope<WhatsAppConversation[]>>(this.base, {
      headers: this.getAuthHeaders(),
      params,
    });
    return {
      data: res.data.success ? res.data.data || [] : [],
      total: res.data.total ?? 0,
      totalPages: res.data.totalPages ?? 1,
    };
  }

  async getThread(
    conversationUuid: string, opts: { before?: string; limit?: number } = {},
  ): Promise<WhatsAppThread | null> {
    const res = await axios.get<ApiEnvelope<WhatsAppThread>>(
      `${this.base}/${conversationUuid}/thread`,
      { headers: this.getAuthHeaders(), params: opts },
    );
    return res.data.success ? res.data.data : null;
  }

  async sendText(conversationUuid: string, body: string): Promise<SendResult> {
    try {
      const res = await axios.post<ApiEnvelope<SendResult>>(
        `${this.base}/${conversationUuid}/send-text`,
        { body },
        { headers: this.getAuthHeaders() },
      );
      return res.data.success ? res.data.data : { ok: false, error: res.data.message };
    } catch (err: any) {
      return this.sendErrorResult(err);
    }
  }

  async sendTemplate(
    conversationUuid: string,
    payload: { template_name: string; language?: string; components?: any[]; preview_text?: string },
  ): Promise<SendResult> {
    try {
      const res = await axios.post<ApiEnvelope<SendResult>>(
        `${this.base}/${conversationUuid}/send-template`,
        payload,
        { headers: this.getAuthHeaders() },
      );
      return res.data.success ? res.data.data : { ok: false, error: res.data.message };
    } catch (err: any) {
      return this.sendErrorResult(err);
    }
  }

  async sendMedia(
    conversationUuid: string, file: File, mediaType: MediaMessageType, caption?: string,
  ): Promise<SendResult> {
    const form = new FormData();
    form.append('file', file);
    form.append('media_type', mediaType);
    if (caption) form.append('caption', caption);
    const { Authorization } = this.getAuthHeaders();
    try {
      const res = await axios.post<ApiEnvelope<SendResult>>(
        `${this.base}/${conversationUuid}/send-media`,
        form,
        { headers: { Authorization } }, // let the browser set the multipart boundary
      );
      return res.data.success ? res.data.data : { ok: false, error: res.data.message };
    } catch (err: any) {
      return this.sendErrorResult(err);
    }
  }

  /** Lead search for the Inbox's "New conversation" flow — name or phone. */
  async searchLeadsForWhatsApp(query: string): Promise<WhatsAppLeadSearchResult[]> {
    if (!query.trim()) return [];
    try {
      const res = await axios.get<ApiEnvelope<WhatsAppLeadSearchResult[]>>(
        `${this.base}/leads-search`,
        { headers: this.getAuthHeaders(), params: { q: query.trim() } },
      );
      return res.data.success ? res.data.data || [] : [];
    } catch {
      return [];
    }
  }

  /** Starts a brand-new conversation (no existing row) by sending the first
   * template — the one send path that creates whatsapp_conversations itself
   * rather than assuming it already exists. */
  async startConversation(
    leadUuid: string,
    payload: { template_name: string; language?: string; components?: any[]; preview_text?: string },
  ): Promise<StartConversationResult> {
    try {
      const res = await axios.post<ApiEnvelope<StartConversationResult>>(
        `${this.base}/start`,
        { lead_uuid: leadUuid, ...payload },
        { headers: this.getAuthHeaders() },
      );
      return res.data.success ? res.data.data : { ok: false, error: res.data.message };
    } catch (err: any) {
      return this.sendErrorResult(err);
    }
  }

  async getQuickContext(conversationUuid: string): Promise<QuickContext | null> {
    try {
      const res = await axios.get<ApiEnvelope<QuickContext>>(
        `${this.base}/${conversationUuid}/quick-context`,
        { headers: this.getAuthHeaders() },
      );
      return res.data.success ? res.data.data : null;
    } catch {
      return null;
    }
  }

  async sendPaymentLink(
    conversationUuid: string,
    payload: { order_uuid: string; amount?: number; expire_in_hours?: number },
  ): Promise<SendResult> {
    try {
      const res = await axios.post<ApiEnvelope<SendResult>>(
        `${this.base}/${conversationUuid}/send-payment-link`,
        payload,
        { headers: this.getAuthHeaders() },
      );
      return res.data.success ? res.data.data : { ok: false, error: res.data.message };
    } catch (err: any) {
      return this.sendErrorResult(err);
    }
  }

  /** Product-catalogue search for the composer's "From this lead" attach menu. */
  async searchProducts(query: string): Promise<CatalogueProduct[]> {
    if (!query.trim()) return [];
    try {
      const res = await axios.get<ApiEnvelope<CatalogueProduct[]>>(
        `${API_BASE_URL}/shopify/products`,
        { headers: this.getAuthHeaders(), params: { search: query.trim() } },
      );
      return res.data.success ? res.data.data || [] : [];
    } catch {
      return [];
    }
  }

  private sendErrorResult(err: any): SendResult {
    const data = err?.response?.data;
    return { ok: false, error: data?.message || err?.message || 'Send failed', window_closed: data?.data?.window_closed };
  }

  async markRead(conversationUuid: string): Promise<boolean> {
    const res = await axios.post<ApiEnvelope<null>>(
      `${this.base}/${conversationUuid}/read`, {}, { headers: this.getAuthHeaders() },
    );
    return res.data.success;
  }

  async markUnread(conversationUuid: string): Promise<boolean> {
    const res = await axios.post<ApiEnvelope<null>>(
      `${this.base}/${conversationUuid}/unread`, {}, { headers: this.getAuthHeaders() },
    );
    return res.data.success;
  }

  async setStatus(conversationUuid: string, status: ConversationStatus): Promise<boolean> {
    const res = await axios.post<ApiEnvelope<null>>(
      `${this.base}/${conversationUuid}/status`, { status }, { headers: this.getAuthHeaders() },
    );
    return res.data.success;
  }

  async setMute(conversationUuid: string, muted: boolean): Promise<boolean> {
    const res = await axios.post<ApiEnvelope<null>>(
      `${this.base}/${conversationUuid}/mute`, { muted }, { headers: this.getAuthHeaders() },
    );
    return res.data.success;
  }

  async listNotes(conversationUuid: string): Promise<WhatsAppInternalNote[]> {
    const res = await axios.get<ApiEnvelope<WhatsAppInternalNote[]>>(
      `${this.base}/${conversationUuid}/notes`, { headers: this.getAuthHeaders() },
    );
    return res.data.success ? res.data.data || [] : [];
  }

  async addNote(conversationUuid: string, noteText: string): Promise<WhatsAppInternalNote | null> {
    const res = await axios.post<ApiEnvelope<{ note_id: number; uuid: string }>>(
      `${this.base}/${conversationUuid}/notes`, { note_text: noteText }, { headers: this.getAuthHeaders() },
    );
    if (!res.data.success) return null;
    return {
      note_id: res.data.data.note_id, uuid: res.data.data.uuid, note_text: noteText,
      user_id_fk: 0, user_uuid: '', created_at: new Date().toISOString(),
    };
  }

  async deleteNote(noteUuid: string): Promise<boolean> {
    const res = await axios.delete<ApiEnvelope<null>>(
      `${this.base}/notes/${noteUuid}`, { headers: this.getAuthHeaders() },
    );
    return res.data.success;
  }

  /** URL for a stored media message — the backend redirects to a short-lived
   * presigned Spaces URL, gated by the same lead_scope check as the list.
   * Kept for reference/debugging; MediaAttachment uses fetchMediaBlob()
   * instead so a 403 can be told apart from a genuinely missing file. */
  mediaUrl(messageId: number): string {
    return `${this.base}/messages/${messageId}/media`;
  }

  /**
   * Fetch a stored media message as a blob, distinguishing WHY it failed —
   * a plain <img src=mediaUrl()> can't do this: the browser's onerror fires
   * identically whether the backend said 403 (no permission) or 404/network
   * error (genuinely gone), so every failure rendered as the same generic
   * "Media unavailable" even when the file was actually fine. Callers should
   * revoke the returned object URL (URL.revokeObjectURL) when done with it.
   */
  async fetchMediaBlob(messageId: number): Promise<
    { status: 'ok'; url: string } | { status: 'denied' | 'unavailable' }
  > {
    try {
      const res = await axios.get(this.mediaUrl(messageId), {
        headers: this.getAuthHeaders(), responseType: 'blob',
      });
      return { status: 'ok', url: URL.createObjectURL(res.data as Blob) };
    } catch (err: any) {
      return { status: err?.response?.status === 403 ? 'denied' : 'unavailable' };
    }
  }
}

export const whatsappInboxService = new WhatsAppInboxService();
export default whatsappInboxService;
